/**
 * AccountsManagementSection — Admin "Accounts" tab (Phase 3.0).
 *
 * Two views, toggled by internal state (no router change):
 *   1. List   — paginated table: name · email · type · last login · balance
 *               with search box + account-type filter + click-through to detail.
 *   2. Detail — back link, account info card, credit grant form (allows
 *               negative for clawbacks), and full per-user ledger.
 *
 * Pagination uses the backend's page/page_size/total/pages metadata. Sorting
 * is server-side (sort + order query params); clicking a column header
 * toggles asc/desc and re-fetches.
 *
 * Notes:
 *   - Credit-grant uses the existing `POST /api/admin/credits` endpoint —
 *     same shape as the legacy Credits tab.
 *   - `account_type` is derived server-side from is_admin + role.
 *   - Status pill: "active" green, "suspended" red (no toggle UI yet).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Search,
  Users,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Award,
  Flag,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import BadgeChip from "@/components/BadgeChip";
import TierAssignmentPanel from "@/components/admin/TierAssignmentPanel";
import CapabilityTogglePanel from "@/components/admin/CapabilityTogglePanel";
import CustomerPlanAssignmentPanel from "@/components/admin/CustomerPlanAssignmentPanel";
import ImpersonatePanel from "@/components/admin/ImpersonatePanel";

const REASON_MAX = 200;
// Page-size selector: dropdown options + key used to persist the
// selection. Validating reads against this whitelist means a tampered
// localStorage value gracefully falls back to the default.
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_STORAGE_KEY = "pp_admin_accounts_page_size";

function readStoredPageSize() {
  try {
    const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && PAGE_SIZE_OPTIONS.includes(n)) return n;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_PAGE_SIZE;
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

// Role-colored type badges (interpretation (i) — every account surfaces
// ALL of its capabilities as a priority-ordered pill group). The
// underlying `account_type` (single primary label) is unchanged on the
// server and remains the basis for sort + filter; this is purely
// presentation. Multi-role users render multiple pills in the priority
// order Admin → PooPartner → Customer.
//
// Saturated fills (Feb 2026 contrast bump). Hexes are LITERALS — NOT
// CSS variables — so they do NOT token-flip in dark mode. This is
// intentional: badge saturation must read the same in both modes.
// All three combos clear WCAG AA (4.5:1) for normal text:
//   Admin      #FFFFFF on #A32D2D → 7.07:1
//   PooPartner #2B3942 on #F89623 → 5.31:1
//   Customer   #FFFFFF on #374751 → 9.62:1
const ROLE_PILL_STYLES = {
  Admin:      { bg: "#A32D2D", fg: "#FFFFFF" },  // deep red — admin capability
  PooPartner: { bg: "#F89623", fg: "#2B3942" },  // brand orange + near-slate text
  Customer:   { bg: "#374751", fg: "#FFFFFF" },  // brand slate + white text
};

// Mirrors backend `_derive_capabilities` ordering (which itself mirrors
// VIEW_PRIORITY from AuthContext). Single source of truth here on the
// client side; the backend already returns capabilities pre-ordered.
const ROLE_PILL_ORDER = ["Admin", "PooPartner", "Customer"];

function RolePill({ role, providerLabel }) {
  const style = ROLE_PILL_STYLES[role];
  if (!style) return null;
  // PooPartner label is renameable via admin Platform Settings; preserve
  // the existing display-label flow-through so a custom partner label
  // applies to the pill text.
  const display = role === "PooPartner" ? providerLabel : role;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: style.bg, color: style.fg }}
      data-testid={`role-pill-${role.toLowerCase()}`}
    >
      {display}
    </span>
  );
}

function RolePillGroup({ capabilities, accountType, providerLabel, testId }) {
  // Defensive: if the backend payload predates the multi-capability
  // surface (shouldn't happen post-Phase-3) the array may be missing or
  // empty — fall back to the single primary `account_type` so the cell
  // never goes blank.
  const source =
    capabilities && capabilities.length > 0
      ? capabilities
      : [accountType].filter(Boolean);
  const roles = ROLE_PILL_ORDER.filter((r) => source.includes(r));
  return (
    <div
      className="inline-flex flex-wrap items-center gap-1"
      data-testid={testId || "role-pill-group"}
    >
      {roles.map((r) => (
        <RolePill key={r} role={r} providerLabel={providerLabel} />
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  // Backlog G — D1: wire format stays "active|suspended", UI surfaces
  // "Inactive" for the latter. Customers / partners read the user-facing
  // label, never the raw value.
  const label = active ? "active" : "inactive";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        active
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
      )}
    >
      {label}
    </span>
  );
}

// Backlog G — flag chip surfaced in the Accounts table row + detail header
// when a user is flagged. Tooltip shows category + truncated note.
function FlagChip({ flag, testId }) {
  if (!flag) return null;
  const note = String(flag.note || "");
  const tip = `${flag.category}: ${note.length > 60 ? note.slice(0, 60) + "…" : note}`;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 rounded-full bg-brand-orange/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange"
            data-testid={testId}
          >
            <Flag className="h-3 w-3" aria-hidden="true" />
            {flag.category}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-xs">
          <p className="text-xs">{tip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Backlog G — Status toggle panel (Active / Inactive).
//
// Lives inside AccountDetailView. AlertDialog confirms the deactivate
// path so admins don't misfire on the 1100+ user list. Reactivate is
// a single-click button (low blast radius). Disables the deactivate
// button when the admin is viewing their OWN account (server returns
// 422 anyway — we just spare the roundtrip).
// ---------------------------------------------------------------------------
function StatusTogglePanel({ detail, currentAdminId, onUpdated }) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isActive = (detail?.status || "active") === "active";
  const isSelf = currentAdminId && currentAdminId === detail?.id;

  const flip = async (newStatus) => {
    setSubmitting(true);
    try {
      const { data } = await api.patch(
        `/admin/accounts/${detail.id}/status`,
        { status: newStatus }
      );
      onUpdated(data);
      toast.success(
        newStatus === "active"
          ? "Account reactivated."
          : "Account deactivated."
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <hr className="my-5 border-pp-border" />
      <section data-testid="account-status-panel" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-heading text-lg font-semibold text-pp-text">
              Account status
            </h4>
            <p className="mt-1 text-sm text-pp-text-muted">
              {isActive
                ? "Active — full access, listings visible, can book."
                : "Inactive — login blocked, listings hidden, booking blocked. Reversible at any time."}
            </p>
          </div>
          {isActive ? (
            <Button
              type="button"
              variant="outline"
              disabled={submitting || isSelf}
              onClick={() => setConfirmOpen(true)}
              className="h-10 rounded-full border-red-300 px-5 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/40"
              data-testid="account-status-deactivate-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ShieldAlert className="mr-2 h-4 w-4" />
              )}
              Deactivate account
            </Button>
          ) : (
            <Button
              type="button"
              disabled={submitting}
              onClick={() => flip("active")}
              className="h-10 rounded-full bg-pp-orange px-5 text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid="account-status-reactivate-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="mr-2 h-4 w-4" />
              )}
              Reactivate account
            </Button>
          )}
        </div>
        {isSelf && isActive && (
          <p
            className="rounded-2xl border border-pp-border bg-pp-surface2 px-4 py-2 text-xs text-pp-text-muted"
            data-testid="account-status-self-hint"
          >
            You can&rsquo;t deactivate your own admin account. Have another
            admin do it.
          </p>
        )}
      </section>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="account-status-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Deactivate {detail?.name || detail?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The user will be blocked from logging in. Customers can&rsquo;t
              book; PooPartners disappear from discovery. Existing bookings
              continue normally. Reactivation restores full access at any
              time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="account-status-cancel-btn"
              disabled={submitting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                flip("suspended");
              }}
              disabled={submitting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="account-status-confirm-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deactivating…
                </>
              ) : (
                "Deactivate"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Backlog G — Flag panel (admin annotation, independent of status).
//
// Single replaceable flag per user. Empty state: "Add flag" button →
// inline form. Filled state: card with category chip + note + metadata
// + Replace / Clear buttons. Replace re-opens the form pre-filled.
// ---------------------------------------------------------------------------
const FLAG_NOTE_MAX = 500;
const FLAG_CATEGORIES = ["Billing", "Account issue", "Troubleshooting", "Other"];

function FlagPanel({ detail, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState(
    detail?.flag?.category || "Billing"
  );
  const [note, setNote] = useState(detail?.flag?.note || "");

  const openForm = (preset) => {
    if (preset) {
      setCategory(preset.category || "Billing");
      setNote(preset.note || "");
    } else {
      setCategory("Billing");
      setNote("");
    }
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setCategory(detail?.flag?.category || "Billing");
    setNote(detail?.flag?.note || "");
  };

  const save = async () => {
    if (!note.trim()) {
      toast.error("Note is required.");
      return;
    }
    if (note.length > FLAG_NOTE_MAX) {
      toast.error(`Note is too long (max ${FLAG_NOTE_MAX}).`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.patch(
        `/admin/accounts/${detail.id}/flag`,
        { category, note: note.trim() }
      );
      onUpdated(data);
      toast.success("Flag saved.");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async () => {
    // window.confirm is lighter-touch than AlertDialog — flag is an
    // annotation, not a soft block, so the bar is lower.
    if (!window.confirm("Clear the flag on this account?")) return;
    setSubmitting(true);
    try {
      const { data } = await api.delete(`/admin/accounts/${detail.id}/flag`);
      onUpdated(data);
      toast.success("Flag cleared.");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const hasFlag = !!detail?.flag;
  const flag = detail?.flag;

  return (
    <>
      <hr className="my-5 border-pp-border" />
      <section data-testid="account-flag-panel" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-heading text-lg font-semibold text-pp-text">
              Flag
            </h4>
            <p className="mt-1 text-sm text-pp-text-muted">
              Admin-only annotation. Independent of account status — a flag
              survives reactivation and is replaced (not stacked) by new
              flags.
            </p>
          </div>
          {!editing && !hasFlag && (
            <Button
              type="button"
              variant="outline"
              onClick={() => openForm(null)}
              className="h-10 rounded-full border-brand-orange/40 px-5 text-brand-orange hover:bg-brand-orange/10"
              data-testid="account-flag-add-btn"
            >
              <Flag className="mr-2 h-4 w-4" />
              Add flag
            </Button>
          )}
        </div>

        {/* Filled state */}
        {!editing && hasFlag && (
          <Card
            className="rounded-2xl border border-pp-border bg-pp-surface2 p-4"
            data-testid={`account-flag-meta-${detail.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="mb-2">
                  <FlagChip flag={flag} testId="account-flag-current-chip" />
                </div>
                <p className="whitespace-pre-wrap break-words text-sm text-pp-text">
                  {flag.note}
                </p>
                <p className="mt-2 text-xs text-pp-text-muted">
                  Flagged by{" "}
                  <strong className="text-pp-text">{flag.admin_email}</strong>{" "}
                  on {formatTs(flag.flagged_at)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openForm(flag)}
                  className="h-9 rounded-full"
                  data-testid="account-flag-replace-btn"
                >
                  Replace flag
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clear}
                  disabled={submitting}
                  className="h-9 rounded-full text-pp-text-muted hover:text-red-600"
                  data-testid="account-flag-clear-btn"
                >
                  {submitting ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <X className="mr-1 h-4 w-4" />
                  )}
                  Clear flag
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Form (add or replace) */}
        {editing && (
          <Card
            className="rounded-2xl border border-pp-border bg-pp-surface2 p-4 space-y-3"
            data-testid="account-flag-form"
          >
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                Category
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  className="mt-2 h-10 rounded-full"
                  data-testid="account-flag-category-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FLAG_CATEGORIES.map((c) => (
                    <SelectItem
                      key={c}
                      value={c}
                      data-testid={`account-flag-category-option-${c
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                Note
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What does this admin need to know?"
                maxLength={FLAG_NOTE_MAX + 50}
                rows={4}
                className="mt-2"
                data-testid="account-flag-note-input"
              />
              <p className="mt-1 text-right text-[11px] text-pp-text-muted">
                {note.length}/{FLAG_NOTE_MAX}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={cancel}
                disabled={submitting}
                className="h-10 rounded-full"
                data-testid="account-flag-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={save}
                disabled={submitting || !note.trim()}
                className="h-10 rounded-full bg-pp-orange px-5 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="account-flag-save-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save flag"
                )}
              </Button>
            </div>
          </Card>
        )}
      </section>
    </>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 16).replace("T", " ");
}

function SortHeader({ field, current, order, onClick, children, className }) {
  const isActive = current === field;
  return (
    <th className={cn("py-2 pr-3 text-left", className)}>
      <button
        type="button"
        onClick={() => onClick(field)}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors",
          isActive ? "text-pp-text" : "text-pp-text-muted hover:text-pp-text"
        )}
        data-testid={`accounts-sort-${field}`}
      >
        {children}
        {isActive &&
          (order === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </button>
    </th>
  );
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------
function AccountsListView({ onSelect }) {
  const { providerDisplayLabel } = useConfig();

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debounceRef = useRef(null);

  const [accountType, setAccountType] = useState("All");
  // Backlog G — additive status filter alongside account_type. Server
  // accepts `status_filter` ∈ {All|Active|Inactive|Flagged}. "Flagged"
  // composes AND with account_type so admins can drill into e.g.
  // "Inactive Customers" by stacking both selects independently.
  const [accountStatus, setAccountStatus] = useState("All");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  // Per-page selector — initialized from localStorage so reload keeps
  // the admin's preference. Whitelist-guarded inside readStoredPageSize().
  const [pageSize, setPageSizeState] = useState(() => readStoredPageSize());

  // Wrapper so changing the dropdown both persists the choice AND
  // resets to page 1 (so admins don't land on an out-of-range page
  // when shrinking the result window — e.g. you were on page 30 with
  // 25/page, now you pick 200/page and there's only 4 pages).
  const setPageSize = (next) => {
    setPageSizeState(next);
    setPage(1);
    try {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next));
    } catch {
      /* see readStoredPageSize — non-fatal */
    }
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });

  // Debounce search input → debouncedQ (250ms).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort,
        order,
      };
      if (debouncedQ.length >= 2) params.q = debouncedQ;
      if (accountType !== "All") params.account_type = accountType;
      if (accountStatus !== "All") params.status_filter = accountStatus;
      const { data: body } = await api.get("/admin/accounts", { params });
      setData(body);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever any query knob changes (incl. pageSize selector).
  useEffect(() => {
    fetchPage();
    // `fetchPage` closes over the deps below + setData/setLoading (stable
    // setters); adding it to deps would re-trigger the effect after every
    // fetch (function identity churns each render) — classic pagination
    // anti-loop pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, accountType, accountStatus, sort, order, page, pageSize]);

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
    setPage(1);
  };

  return (
    <section className="mt-2" data-testid="accounts-section">
      <header className="mb-5">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-brand-orange" aria-hidden="true" />
          <h2 className="font-heading text-2xl font-semibold text-pp-text">Accounts</h2>
        </div>
        <p className="mt-1 text-sm text-pp-text-muted">
          Every customer, {providerDisplayLabel}, and admin account on the platform. Click a
          row for credit grants and details.
        </p>
      </header>

      {/* --- Filter bar -------------------------------------------------- */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Search name or email
          </Label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-pp-text-muted" />
            <Input
              type="text"
              placeholder="e.g. customer@…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-11 rounded-full pl-12 pr-5"
              data-testid="accounts-search-input"
            />
          </div>
        </div>
        <div className="w-[200px]">
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Account type
          </Label>
          <Select
            value={accountType}
            onValueChange={(v) => {
              setAccountType(v);
              setPage(1);
            }}
          >
            <SelectTrigger
              className="mt-2 h-11 rounded-full"
              data-testid="accounts-type-filter-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All" data-testid="accounts-type-filter-all">All</SelectItem>
              <SelectItem value="Admin">Admin</SelectItem>
              <SelectItem value="Customer">Customer</SelectItem>
              <SelectItem value="PooPartner">{providerDisplayLabel}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[200px]">
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Status
          </Label>
          <Select
            value={accountStatus}
            onValueChange={(v) => {
              setAccountStatus(v);
              setPage(1);
            }}
          >
            <SelectTrigger
              className="mt-2 h-11 rounded-full"
              data-testid="accounts-status-filter-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All" data-testid="accounts-status-filter-all">All</SelectItem>
              <SelectItem value="Active" data-testid="accounts-status-filter-active">Active</SelectItem>
              <SelectItem value="Inactive" data-testid="accounts-status-filter-inactive">Inactive</SelectItem>
              <SelectItem value="Flagged" data-testid="accounts-status-filter-flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* --- Table ------------------------------------------------------- */}
      <Card
        className="rounded-2xl border border-pp-border bg-pp-surface p-0 overflow-hidden"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
        data-testid="accounts-list-card"
      >
        {loading ? (
          <p className="flex items-center gap-2 p-6 text-sm text-pp-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading accounts…
          </p>
        ) : data.items.length === 0 ? (
          <p
            className="p-10 text-center text-sm text-pp-text-muted"
            data-testid="accounts-empty"
          >
            No accounts match those filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="accounts-table">
              <thead className="border-b border-pp-border bg-pp-surface2">
                <tr>
                  <SortHeader
                    field="name"
                    current={sort}
                    order={order}
                    onClick={toggleSort}
                    className="pl-5"
                  >
                    Name
                  </SortHeader>
                  <SortHeader field="email" current={sort} order={order} onClick={toggleSort}>
                    Email
                  </SortHeader>
                  <SortHeader
                    field="account_type"
                    current={sort}
                    order={order}
                    onClick={toggleSort}
                  >
                    Type
                  </SortHeader>
                  <SortHeader
                    field="last_login_at"
                    current={sort}
                    order={order}
                    onClick={toggleSort}
                  >
                    Last logged in
                  </SortHeader>
                  <SortHeader
                    field="credit_balance"
                    current={sort}
                    order={order}
                    onClick={toggleSort}
                  >
                    Balance
                  </SortHeader>
                  <th className="py-2 pr-5 text-right text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => onSelect(it.id)}
                    className="cursor-pointer border-t border-pp-border align-middle transition-colors hover:bg-pp-surface2"
                    data-testid={`accounts-row-${it.id}`}
                  >
                    <td className="py-3 pl-5 pr-3 font-semibold text-pp-text">
                      {it.name || "(unnamed)"}
                    </td>
                    <td className="py-3 pr-3 text-pp-text-muted">{it.email}</td>
                    <td className="py-3 pr-3">
                      <RolePillGroup
                        capabilities={it.capabilities}
                        accountType={it.account_type}
                        providerLabel={providerDisplayLabel}
                        testId={`accounts-type-pill-group-${it.id}`}
                      />
                    </td>
                    <td className="py-3 pr-3 text-pp-text-muted">
                      {formatTs(it.last_login_at)}
                    </td>
                    <td className="py-3 pr-3 font-mono tabular-nums font-semibold text-pp-text">
                      ${Number(it.credit_balance || 0).toFixed(2)}
                    </td>
                    <td className="py-3 pr-5 text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        <StatusPill status={it.status} />
                        <FlagChip
                          flag={it.flag}
                          testId={`accounts-row-flag-chip-${it.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* --- Pagination -------------------------------------------------- */}
      {data.total > 0 && (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-pp-text-muted"
          data-testid="accounts-pagination"
        >
          <span>
            Showing page <strong className="text-pp-text">{data.page}</strong> of{" "}
            <strong className="text-pp-text">{data.pages}</strong> ·{" "}
            <strong className="text-pp-text">{data.total}</strong> total
          </span>
          <div className="flex flex-wrap items-center gap-3">
            {/* Per-page selector — persists to localStorage, resets to
                page 1, preserves search/filter/sort. */}
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                Per page
              </Label>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => setPageSize(Number(v))}
              >
                <SelectTrigger
                  className="h-9 w-[90px] rounded-full"
                  data-testid="accounts-page-size-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem
                      key={n}
                      value={String(n)}
                      data-testid={`accounts-page-size-option-${n}`}
                    >
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="ghost"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-9 rounded-full"
              data-testid="accounts-page-prev"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={page >= data.pages || loading}
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              className="h-9 rounded-full"
              data-testid="accounts-page-next"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------
function AccountDetailView({ userId, onBack }) {
  const { providerDisplayLabel, creditLabel } = useConfig();
  // Backlog G — current admin id is needed so the StatusTogglePanel can
  // disable the deactivate button on the admin's OWN account (mirrors
  // the server-side 422 guard but avoids a roundtrip).
  const { user: currentAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/accounts/${userId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // `reload` reads `userId` (already a dep) + setDetail/setLoading
    // (stable setters); its identity churns each render. Adding it to
    // deps would infinite-loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // --- Credit grant form -------------------------------------------------
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const amountNumber = useMemo(() => Number(amount), [amount]);
  const currentBalance = Number(detail?.credit_balance || 0);
  const projectedBalance = useMemo(
    () => Math.round((currentBalance + (Number.isFinite(amountNumber) ? amountNumber : 0)) * 100) / 100,
    [currentBalance, amountNumber]
  );
  const amountError = useMemo(() => {
    if (amount === "" || amount === null || amount === undefined) return "Required.";
    if (!Number.isFinite(amountNumber)) return "Number required.";
    if (amountNumber === 0) return "Must be non-zero.";
    // Client-side mirror of the server-side insufficient-balance guard:
    // refuse any debit that would land below zero, naming the maximum
    // allowed debit so the admin can correct the value without a round-trip.
    if (amountNumber < 0 && projectedBalance < 0) {
      return `Insufficient balance. Maximum debit: $${currentBalance.toFixed(2)}.`;
    }
    return null;
  }, [amount, amountNumber, projectedBalance, currentBalance]);
  const reasonError = useMemo(() => {
    const s = reason.trim();
    if (!s) return "Required.";
    if (s.length > REASON_MAX) return `${REASON_MAX} characters max.`;
    return null;
  }, [reason]);
  const formValid = !amountError && !reasonError;

  const submit = async () => {
    if (!detail || !formValid) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/credits", {
        user_id: detail.id,
        amount: amountNumber,
        reason: reason.trim(),
      });
      toast.success(
        `Credit applied — new balance: $${Number(data.new_balance).toFixed(2)}`
      );
      setAmount("");
      setReason("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !detail) {
    return (
      <section className="mt-2" data-testid="account-detail-section">
        <button
          type="button"
          onClick={onBack}
          className="mb-5 inline-flex items-center gap-1 text-sm font-semibold text-pp-text-muted transition-colors hover:text-brand-orange"
          data-testid="account-detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to accounts
        </button>
        <p className="flex items-center gap-2 text-sm text-pp-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading account…
        </p>
      </section>
    );
  }

  // Address summary for customer profile (read-only, just informational).
  // Capability-flag aware (was account_type-string previously) so multi-role
  // users surface BOTH customer + provider sections.
  const isCustomer = !!detail.is_customer;
  const isProvider = !!detail.is_provider;
  const customerProfileSummary = detail.customer_profile || (isCustomer ? detail.profile : null) || {};
  const providerProfileSummary = detail.provider_profile || (isProvider ? detail.profile : null) || {};
  const profile = customerProfileSummary; // legacy single-profile alias for the existing customer block

  return (
    <section className="mt-2" data-testid="account-detail-section">
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1 text-sm font-semibold text-pp-text-muted transition-colors hover:text-brand-orange"
        data-testid="account-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to accounts
      </button>

      <Card
        className="rounded-2xl border border-pp-border bg-pp-surface p-6"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
        data-testid="account-detail-card"
      >
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className="font-heading text-2xl font-bold text-pp-text"
                data-testid="account-detail-name"
              >
                {detail.name || detail.email}
              </h3>
              <RolePillGroup
                capabilities={detail.capabilities}
                accountType={detail.account_type}
                providerLabel={providerDisplayLabel}
                testId="account-detail-type-pill-group"
              />
              <StatusPill status={detail.status} />
              <FlagChip
                flag={detail.flag}
                testId={`account-flag-chip-${detail.id}`}
              />
            </div>
            <p className="mt-1 text-sm text-pp-text-muted" data-testid="account-detail-email">
              {detail.email}
            </p>
            <p className="mt-1 text-xs text-pp-text-muted">
              Created {formatTs(detail.created_at)} · Last login{" "}
              <span data-testid="account-detail-last-login">{formatTs(detail.last_login_at)}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
              {creditLabel}
            </p>
            <p
              className="font-heading text-3xl font-bold text-brand-orange"
              data-testid="account-detail-balance"
            >
              ${Number(detail.credit_balance || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Profile summary (read-only) */}
        {(isCustomer || isProvider) && (
          <>
            <hr className="my-5 border-pp-border" />
            <div className="grid gap-4 text-sm sm:grid-cols-2" data-testid="account-detail-profile">
              {isCustomer && (
                <>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                      Address
                    </p>
                    <p className="mt-1 text-pp-text">
                      {profile.address_line1 || "—"}
                      {profile.address_line2 ? `, ${profile.address_line2}` : ""}
                      {profile.city ? ` · ${profile.city}` : ""}
                      {profile.state ? `, ${profile.state}` : ""}
                      {profile.zip ? ` ${profile.zip}` : ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                      Yard / Dogs
                    </p>
                    <p className="mt-1 text-pp-text">
                      {profile.yard_size || "—"} yard · {profile.dog_count ?? 0} dog
                      {(profile.dog_count ?? 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                </>
              )}
              {isProvider && (
                <>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                      Business
                    </p>
                    <p className="mt-1 text-pp-text">{providerProfileSummary.business_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                      Service ZIPs
                    </p>
                    <p className="mt-1 text-pp-text">
                      {(providerProfileSummary.service_zips || []).join(", ") || "—"}
                    </p>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Backlog D — Impersonation "Act as user" panel. Lives ABOVE
            the status/flag/capability panels so the entry point is
            the first action surface on every detail view. The panel
            self-disables for admin targets / self / already-active
            impersonation states. */}
        <hr className="my-5 border-pp-border" />
        <ImpersonatePanel detail={detail} />

        {/* Backlog G — Account status panel (active / inactive toggle).
            Lives ABOVE the capability panel so admins see the soft-block
            control before the role controls. Confirms via AlertDialog
            to avoid misfires on the 1100+ user list. */}
        <StatusTogglePanel
          detail={detail}
          currentAdminId={currentAdmin?.id}
          onUpdated={(updated) => setDetail(updated)}
        />

        {/* Backlog G — Flag panel (admin annotation, independent of status). */}
        <FlagPanel
          detail={detail}
          onUpdated={(updated) => setDetail(updated)}
        />

        {/* Tier assignment — PooPartner only. Render BELOW the profile
            summary, ABOVE the credit grant. Skipped automatically by the
            component for Customer / Admin accounts. */}
        {isProvider && (
          <TierAssignmentPanel detail={detail} onSaved={(updated) => setDetail(updated)} />
        )}

        {/* Customer plan assignment — Customer-capability only. Hidden by
            the component itself for non-customer accounts. */}
        <CustomerPlanAssignmentPanel
          detail={detail}
          onSaved={(updated) => setDetail(updated)}
        />

        {/* Capability toggles — render for EVERY account so admins can
            promote a single-role user into a multi-role account (or vice
            versa). Server enforces last-role + admin-self-demote guards. */}
        <CapabilityTogglePanel
          detail={detail}
          onSaved={(updated) => setDetail(updated)}
        />

        {/* Credit grant form — only on accounts that carry a balance */}
        {(isCustomer || isProvider) && (
          <>
            <hr className="my-5 border-pp-border" />
            <h4 className="mb-3 font-heading text-lg font-semibold text-pp-text">
              Grant or adjust {creditLabel}
            </h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                  Amount
                </Label>
                <div className="relative mt-2">
                  <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 10 or -5 (clawback)"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={cn(
                      "h-11 rounded-full pl-9 pr-5",
                      amount !== "" && amountError && "border-red-500 focus-visible:ring-red-500"
                    )}
                    data-testid="account-grant-amount-input"
                  />
                </div>
                {amount !== "" && amountError && (
                  <p
                    className="mt-1 text-xs font-semibold text-red-600"
                    data-testid="account-grant-amount-error"
                  >
                    {amountError}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                  Reason
                </Label>
                <Input
                  type="text"
                  placeholder="why this adjustment?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={cn(
                    "mt-2 h-11 rounded-full px-5",
                    reason !== "" && reasonError && "border-red-500 focus-visible:ring-red-500"
                  )}
                  maxLength={REASON_MAX + 50}
                  data-testid="account-grant-reason-input"
                />
                <p className="mt-1 text-right text-[11px] text-pp-text-muted">
                  {reason.length}/{REASON_MAX}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                onClick={submit}
                disabled={submitting || !formValid}
                className="h-11 rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="account-grant-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Applying…
                  </>
                ) : (
                  "Apply credit"
                )}
              </Button>
            </div>
          </>
        )}

        {/* Per-user ledger */}
        <hr className="my-5 border-pp-border" />
        <h4 className="mb-3 font-heading text-lg font-semibold text-pp-text">
          Recent ledger
        </h4>
        {detail.recent_ledger.length === 0 ? (
          <p
            className="rounded-2xl border border-dashed border-pp-border p-6 text-center text-sm text-pp-text-muted"
            data-testid="account-detail-ledger-empty"
          >
            No activity yet.
          </p>
        ) : (
          <div className="overflow-x-auto" data-testid="account-detail-ledger-table">
            <table className="w-full text-sm">
              <thead className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                <tr>
                  <th className="py-2 pr-3 text-left">Date</th>
                  <th className="py-2 pr-3 text-left">Amount</th>
                  <th className="py-2 pr-3 text-left">Reason</th>
                  <th className="py-2 pr-3 text-left">Admin</th>
                </tr>
              </thead>
              <tbody>
                {detail.recent_ledger.map((e) => {
                  const positive = e.amount >= 0;
                  return (
                    <tr
                      key={e.id}
                      className="border-t border-pp-border align-top"
                      data-testid={`account-ledger-row-${e.id}`}
                    >
                      <td className="py-3 pr-3 text-pp-text-muted">{formatTs(e.created_at)}</td>
                      <td
                        className={cn(
                          "py-3 pr-3 font-mono tabular-nums font-semibold",
                          positive ? "text-brand-orange" : "text-slate-500 dark:text-slate-400"
                        )}
                      >
                        {positive ? "+" : "−"}${Math.abs(e.amount).toFixed(2)}
                      </td>
                      <td className="py-3 pr-3 text-pp-text">{e.reason}</td>
                      <td className="py-3 pr-3 text-pp-text-muted">{e.admin_email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Public wrapper — toggles list/detail based on internal state
// ---------------------------------------------------------------------------
export default function AccountsManagementSection() {
  const [selectedId, setSelectedId] = useState(null);
  if (selectedId) {
    return (
      <AccountDetailView
        userId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }
  return <AccountsListView onSelect={setSelectedId} />;
}
